import { useCallback, useContext, useMemo } from "react"
import { useParams } from "react-router-dom"
import { useDeleteConfirm } from "../../../../../hooks/useDeleteConfirm"
import { useTrainingPermissions } from "../../../../../hooks/useTrainingPermissions"
import { DiMaProgress } from "../../../../components/DiMaProgress"
import { UserContext } from "../../../../context/UserContext"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToApplicationFeature,
} from "../../../../models/Authorization"
import { TrainingGet } from "../../../../models/Training"
import { TrainingPermissionGet } from "../../../../models/TrainingPermission"
import { deleteEntity } from "../../../../utils/deleteEntity"
import { DiMaAddButton } from "../../../partials/DiMaAddButton"
import { EntityRow, EntityTab } from "../../../partials/EntityTab"

export function TrainingPermissions(props: { readonly training: TrainingGet }) {
  const { trainingId } = useParams()
  const { training } = props
  const { user, isLoadingUser } = useContext(UserContext)
  const { requestDelete, DeleteConfirmDialog } = useDeleteConfirm()
  const { trainingPermissions, isLoadingTrainingPermissions } =
    useTrainingPermissions(trainingId)

  const toolbar = useMemo(
    () => (
      <DiMaAddButton
        url={`/trainings/${trainingId}/permissions/create`}
        feature={DimaFeatureShortNameEnum.TM}
        applicationId={training.application.id}
      />
    ),
    [trainingId, training.application.id]
  )

  const handleActionMenuClick = useCallback(
    (item: { title: string }, row: EntityRow) => {
      if (item.title === "Delete") {
        requestDelete(() =>
          deleteEntity(`/trainings/${trainingId}/permissions/${row.id}`)
        )
      }
    },
    [trainingId, requestDelete]
  )

  if (isLoadingUser || !user) {
    return <DiMaProgress />
  }

  const columns = [{ field: "name", headerName: "Permission" }]

  const sorted = [...trainingPermissions].sort((a, b) =>
    (a.permission?.name ?? "").localeCompare(b.permission?.name ?? "")
  )

  const rows = sorted.map((permission: TrainingPermissionGet) => ({
    id: permission.permission.id,
    name: permission.permission.name,
  }))

  const canDelete = hasUserAccessToApplicationFeature(
    user.authorizations.applications,
    training.application.id,
    DimaFeatureShortNameEnum.TM,
    AccessLevel.Write
  )

  return (
    <>
      {DeleteConfirmDialog}
      <EntityTab
        rows={rows}
        cols={columns}
        isLoading={isLoadingTrainingPermissions}
        toolbar={toolbar}
        actionItems={
          canDelete
            ? [{ title: "Delete", iconName: "icon_abb_dias icon-abb_delete" }]
            : []
        }
        onActionMenuClick={handleActionMenuClick}
      />
    </>
  )
}
